import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { format, parseISO } from "date-fns";
import { appointmentLeadName, listOpenAppointments } from "@/services/appointments";

/** Reminder window: toast when an appointment starts within the next hour. */
const DUE_SOON_MS = 60 * 60 * 1000;
const CHECK_INTERVAL_MS = 60 * 1000;

function notifiedKey(): string {
  return "myroute:appt:notified:v1";
}

function readNotified(): Set<string> {
  try {
    const raw = sessionStorage.getItem(notifiedKey());
    return new Set(raw ? (JSON.parse(raw) as string[]) : []);
  } catch {
    return new Set();
  }
}

function persistNotified(ids: Set<string>): void {
  try {
    sessionStorage.setItem(notifiedKey(), JSON.stringify([...ids]));
  } catch {
    // ignore — worst case a duplicate toast next session
  }
}

/**
 * In-app appointment reminders. Mounted once in AppShell (like
 * useRealtimeSync) so reminders fire on any page, not just the dashboard.
 * Dedupe is per browser session via sessionStorage — a due-soon appointment
 * toasts once, not every poll tick.
 */
export function useAppointmentReminders(enabled: boolean) {
  const notifiedRef = useRef<Set<string> | null>(null);

  const apptsQuery = useQuery({
    queryKey: ["appointments", "open"],
    queryFn: () => listOpenAppointments(),
    enabled,
    // Realtime invalidation keeps this fresh; the interval below only
    // re-evaluates "due soon" against the already-cached rows.
    staleTime: 30_000,
    refetchInterval: 5 * 60_000,
  });

  const appts = apptsQuery.data;

  useEffect(() => {
    if (!enabled || !appts?.length) return;
    if (!notifiedRef.current) notifiedRef.current = readNotified();

    function check() {
      const notified = notifiedRef.current!;
      const now = Date.now();
      let dirty = false;
      for (const a of appts ?? []) {
        const at = parseISO(a.scheduled_at).getTime();
        const msUntil = at - now;
        if (msUntil <= 0 || msUntil > DUE_SOON_MS || notified.has(a.id)) continue;
        notified.add(a.id);
        dirty = true;
        toast(
          `Appointment soon: ${appointmentLeadName(a)} at ${format(parseISO(a.scheduled_at), "h:mm a")}`,
          { icon: "📅", duration: 10_000, id: `appt-due-${a.id}` },
        );
      }
      if (dirty) persistNotified(notified);
    }

    check();
    const timer = setInterval(check, CHECK_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [enabled, appts]);
}
